// Clip filtering and diversity-first ordering utilities.

import type { Clip } from '@airhead-games/shared/types/configs/media/clipLibrary';
import { shuffleArray } from '@airhead-games/shared/utilities';

export const DEFAULT_CLIP_BATCH_SIZE = 4;

export interface ClipFilterOptions {
  gameIds?: string[];
  streamerNames?: string[];
}

export interface ClipPlanOptions extends ClipFilterOptions {
  batchSize?: number;
}

export type ClipDiversityAxis = 'gameId' | 'streamerName';

type ClipAxisValueSets = Record<ClipDiversityAxis, Set<string>>;
type ClipScore = [number, number, number];

const createAxisValueSets = (): ClipAxisValueSets => ({
  gameId: new Set<string>(),
  streamerName: new Set<string>(),
});

const getClipAxisValue = (clip: Clip, axis: ClipDiversityAxis): string =>
  axis === 'gameId' ? clip.gameId : clip.streamerName;

const getUniqueClipAxisCount = (
  clips: Clip[],
  axis: ClipDiversityAxis
): number => new Set(clips.map((clip) => getClipAxisValue(clip, axis))).size;

const compareScores = (left: ClipScore, right: ClipScore): number => {
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return left[index] - right[index];
    }
  }

  return 0;
};

const markSeenValues = (
  seenValues: ClipAxisValueSets,
  axes: ClipDiversityAxis[],
  clip: Clip
) => {
  axes.forEach((axis) => {
    seenValues[axis].add(getClipAxisValue(clip, axis));
  });
};

const getClipScore = (
  clip: Clip,
  axes: ClipDiversityAxis[],
  batchSeenValues: ClipAxisValueSets,
  priorBatchSeenValues: ClipAxisValueSets
): ClipScore => {
  let newValuesInBatch = 0;
  let newValuesInBatchAndHistory = 0;
  let newValuesInHistory = 0;

  axes.forEach((axis) => {
    const value = getClipAxisValue(clip, axis);
    const isNewInBatch = !batchSeenValues[axis].has(value);
    const isNewInHistory = !priorBatchSeenValues[axis].has(value);

    if (isNewInBatch) {
      newValuesInBatch += 1;
    }

    if (isNewInHistory) {
      newValuesInHistory += 1;
    }

    if (isNewInBatch && isNewInHistory) {
      newValuesInBatchAndHistory += 1;
    }
  });

  return [newValuesInBatch, newValuesInBatchAndHistory, newValuesInHistory];
};

const selectNextClipIndex = (
  remainingClips: Clip[],
  axes: ClipDiversityAxis[],
  batchSeenValues: ClipAxisValueSets,
  priorBatchSeenValues: ClipAxisValueSets
): number => {
  let bestClipIndex = 0;
  let bestScore: ClipScore = [-1, -1, -1];

  remainingClips.forEach((clip, index) => {
    const clipScore = getClipScore(
      clip,
      axes,
      batchSeenValues,
      priorBatchSeenValues
    );

    if (compareScores(clipScore, bestScore) > 0) {
      bestClipIndex = index;
      bestScore = clipScore;
    }
  });

  return bestClipIndex;
};

export function filterClips(
  clips: Clip[],
  options: ClipFilterOptions = {}
): Clip[] {
  const gameIdSet =
    options.gameIds && options.gameIds.length > 0
      ? new Set(options.gameIds)
      : null;
  const streamerNameSet =
    options.streamerNames && options.streamerNames.length > 0
      ? new Set(options.streamerNames)
      : null;

  if (!gameIdSet && !streamerNameSet) {
    return clips;
  }

  return clips.filter((clip) => {
    if (gameIdSet && !gameIdSet.has(clip.gameId)) {
      return false;
    }

    if (streamerNameSet && !streamerNameSet.has(clip.streamerName)) {
      return false;
    }

    return true;
  });
}

export function getClipDiversityAxes(
  clips: Clip[],
  options: ClipFilterOptions = {}
): ClipDiversityAxis[] {
  const unconstrainedAxes: ClipDiversityAxis[] = [];
  const fallbackAxes: ClipDiversityAxis[] = [];
  const constrainedAxes = {
    gameId: Boolean(options.gameIds?.length),
    streamerName: Boolean(options.streamerNames?.length),
  } satisfies Record<ClipDiversityAxis, boolean>;

  (['gameId', 'streamerName'] as ClipDiversityAxis[]).forEach((axis) => {
    if (getUniqueClipAxisCount(clips, axis) <= 1) {
      return;
    }

    fallbackAxes.push(axis);

    if (!constrainedAxes[axis]) {
      unconstrainedAxes.push(axis);
    }
  });

  return unconstrainedAxes.length > 0 ? unconstrainedAxes : fallbackAxes;
}

export function planClipOrder(
  clips: Clip[],
  options: ClipPlanOptions = {}
): Clip[] {
  const filteredClips = filterClips(clips, options);
  const shuffledClips = shuffleArray(filteredClips);
  const diversityAxes = getClipDiversityAxes(shuffledClips, options);
  const batchSize = Math.max(
    1,
    Math.floor(options.batchSize ?? DEFAULT_CLIP_BATCH_SIZE)
  );

  if (shuffledClips.length <= 1 || diversityAxes.length === 0) {
    return shuffledClips;
  }

  const remainingClips = [...shuffledClips];
  const orderedClips: Clip[] = [];
  const priorBatchSeenValues = createAxisValueSets();

  while (remainingClips.length > 0) {
    const batchSeenValues = createAxisValueSets();
    const nextBatch: Clip[] = [];

    while (nextBatch.length < batchSize && remainingClips.length > 0) {
      const nextClipIndex = selectNextClipIndex(
        remainingClips,
        diversityAxes,
        batchSeenValues,
        priorBatchSeenValues
      );
      const [nextClip] = remainingClips.splice(nextClipIndex, 1);

      nextBatch.push(nextClip);
      markSeenValues(batchSeenValues, diversityAxes, nextClip);
    }

    nextBatch.forEach((clip) => {
      markSeenValues(priorBatchSeenValues, diversityAxes, clip);
    });
    orderedClips.push(...nextBatch);
  }

  return orderedClips;
}
